"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const ts_invariant_1 = require("ts-invariant");
const utils_1 = require("./utils");
const ViewPortMath_1 = require("./ViewPortMath");
const DEFAULT_BOUNDS = [0.001, 100];
var StopAnimationKind;
(function (StopAnimationKind) {
    StopAnimationKind["FORCE"] = "FORCE";
    StopAnimationKind["INTERRUPT"] = "INTERRUPT";
})(StopAnimationKind || (StopAnimationKind = {}));
/**
 * This class has methods to change and animate what portion of the virtual
 * space is visible in the `ViewPort`. You should not construct this directly,
 * rather get an instance from the `ViewPort`.
 */
class ViewPortCamera {
    /**
     * This is only intended to be constructed by the `ViewPort`. Get an instance
     * via the `viewPort` property.
     */
    constructor(values, onUpdated) {
        this.values = values;
        this.onUpdated = onUpdated;
        this.copyValues = (values, to) => {
            // Its faster to do it this way rather than use Object.assign, though it
            // probably doesn't matter much.
            to.centerX = values.centerX;
            to.centerY = values.centerY;
            to.containerHeight = values.containerHeight;
            to.containerWidth = values.containerWidth;
            to.height = values.height;
            to.left = values.left;
            to.width = values.width;
            to.top = values.top;
            to.zoomFactor = values.zoomFactor;
        };
        this.dealWithBoundsChanges = () => {
            this.derivedBounds = Object.assign(Object.assign({}, this.derivedBounds), ViewPortMath_1.ViewPortMath.deriveActualZoomBounds(this.workingValues, this.derivedBounds, DEFAULT_BOUNDS));
            ViewPortMath_1.ViewPortMath.updateBounds(this.workingValues, this.derivedBounds);
            this.scheduleHardUpdate();
        };
        this.handleAnimationFrame = (time) => {
            var _a, _b;
            this.animationFrameId = undefined;
            if (this.animation) {
                if (this.animation.startingTimeMilliseconds === undefined) {
                    this.animation.startingTimeMilliseconds = time - 1000 / 60; // Pretending like we are one frame into the animation
                }
                const completionPercent = (time - this.animation.startingTimeMilliseconds) / this.animation.durationMilliseconds;
                this.advanceCurrentAnimation(completionPercent);
                if (completionPercent < 1) {
                    if (!this.animationFrameId) {
                        this.animationFrameId = requestAnimationFrame(this.handleAnimationFrame);
                    }
                }
                else {
                    this.animation = undefined;
                }
            }
            this.copyValues(this.workingValues, this.values);
            (_b = (_a = this).onUpdated) === null || _b === void 0 ? void 0 : _b.call(_a);
        };
        const { containerWidth, containerHeight, centerX, centerY, left, top, width, height, zoomFactor } = values;
        this.workingValues = {
            containerWidth,
            containerHeight,
            centerX,
            centerY,
            left,
            top,
            width,
            height,
            zoomFactor,
        };
        // Semi-sane default bounds...
        this.derivedBounds = { zoom: DEFAULT_BOUNDS };
    }
    centerFitAreaIntoView(area, additionalBounds, animationOptions) {
        if (!this.stopCurrentAnimation(StopAnimationKind.INTERRUPT)) {
            return;
        }
        const updateTarget = !animationOptions ? this.workingValues : Object.assign({}, this.workingValues);
        ViewPortMath_1.ViewPortMath.centerFitArea(updateTarget, this.derivedBounds, area, additionalBounds);
        if (!animationOptions) {
            this.scheduleHardUpdate();
        }
        else {
            this.scheduleAnimation(updateTarget, animationOptions);
        }
    }
    centerFitHorizontalAreaIntoView(left, width, additionalBounds, animationOptions) {
        if (!this.stopCurrentAnimation(StopAnimationKind.INTERRUPT)) {
            return;
        }
        const updateTarget = !animationOptions ? this.workingValues : Object.assign({}, this.workingValues);
        ViewPortMath_1.ViewPortMath.centerFitHorizontalArea(updateTarget, this.derivedBounds, left, width, additionalBounds);
        if (!animationOptions) {
            this.scheduleHardUpdate();
        }
        else {
            this.scheduleAnimation(updateTarget, animationOptions);
        }
    }
    destroy() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = undefined;
        }
    }
    moveBy(dx, dy, dZoom, anchorContainerX, anchorContainerY, animationOptions) {
        if (!this.stopCurrentAnimation(StopAnimationKind.INTERRUPT)) {
            return;
        }
        const updateTarget = !animationOptions ? this.workingValues : Object.assign({}, this.workingValues);
        ViewPortMath_1.ViewPortMath.updateBy(updateTarget, this.derivedBounds, dx, dy, dZoom, anchorContainerX, anchorContainerY);
        if (!animationOptions) {
            this.scheduleHardUpdate();
        }
        else {
            this.scheduleAnimation(updateTarget, animationOptions);
        }
    }
    moveByInClientSpace(dx, dy, dZoom, anchorContainerX, anchorContainerY, animationOptions) {
        this.moveBy(dx / this.workingValues.zoomFactor, dy / this.workingValues.zoomFactor, dZoom, anchorContainerX, anchorContainerY, animationOptions);
    }
    moveWithDeceleration(vx, vy, friction, preventInterruption) {
        if (!this.stopCurrentAnimation(StopAnimationKind.INTERRUPT)) {
            return;
        }
        const finalFriction = Math.min(Math.abs(friction), 0.99);
        const updateTarget = Object.assign({}, this.workingValues);
        // Figure out roughly how many animation frames we need, where we decrease
        // the velocity by the friction on each frame, to get to 0 velocity. Also
        // keep track of how far we go.
        let currentVX = vx;
        let currentVY = vy;
        let dx = vx;
        let dy = vy;
        let animationFramesNeeded = 1; // Skip the first frame since its trivial
        while (Math.abs(currentVX) > 0.2 || Math.abs(currentVY) > 0.2) {
            currentVX *= finalFriction;
            dx += currentVX;
            currentVY *= finalFriction;
            dy += currentVY;
            animationFramesNeeded++;
        }
        ViewPortMath_1.ViewPortMath.updateBy(updateTarget, this.derivedBounds, dx, dy, 0);
        const animationOptions = {
            preventInterruption,
            durationMilliseconds: (1000 / 60) * animationFramesNeeded,
        };
        this.scheduleAnimation(updateTarget, animationOptions);
    }
    moveWithDecelerationInClientSpace(vx, vy, friction = 0.84, preventInterruption) {
        this.moveWithDeceleration(vx / this.workingValues.zoomFactor, vy / this.workingValues.zoomFactor, friction, preventInterruption);
    }
    /**
     * This is not intended to be called by code outside of react-zoomable-ui itself.
     */
    handleContainerSizeChanged(width, height) {
        if (width === this.workingValues.containerWidth && height === this.workingValues.containerHeight) {
            return;
        }
        // We don't know how to deal with this when an animation is in progress so
        // we either cancel it or run it to completion.
        this.stopCurrentAnimation(StopAnimationKind.FORCE);
        // This is intended to handle the case where we first get our container dimensions
        const wasZeroWidthHeightCenter = this.workingValues.width === 0 &&
            this.workingValues.height === 0 &&
            this.workingValues.centerX === 0 &&
            this.workingValues.centerY === 0;
        this.workingValues.containerWidth = width;
        this.workingValues.containerHeight = height;
        this.workingValues.width = this.workingValues.containerWidth / this.workingValues.zoomFactor;
        this.workingValues.height = this.workingValues.containerHeight / this.workingValues.zoomFactor;
        if (wasZeroWidthHeightCenter) {
            this.workingValues.centerX = this.workingValues.width / 2;
            this.workingValues.centerY = this.workingValues.height / 2;
        }
        // The new container height and width may influence the actual z bounds
        this.dealWithBoundsChanges();
    }
    recenter(x, y, newZoomFactor, animationOptions) {
        if (!this.stopCurrentAnimation(StopAnimationKind.INTERRUPT)) {
            return;
        }
        const updateTarget = !animationOptions ? this.workingValues : Object.assign({}, this.workingValues);
        if (newZoomFactor) {
            ViewPortMath_1.ViewPortMath.updateZoom(updateTarget, this.derivedBounds, newZoomFactor);
        }
        ViewPortMath_1.ViewPortMath.updateTopLeft(updateTarget, this.derivedBounds, x - updateTarget.width / 2, y - updateTarget.height / 2);
        if (!animationOptions) {
            this.scheduleHardUpdate();
        }
        else {
            this.scheduleAnimation(updateTarget, animationOptions);
        }
    }
    /**
     * This is not intended to be called by code outside of react-zoomable-ui
     * itself. It is hidden in the `ViewPortCameraInterface` that is exported
     * from this library.
     */
    setBounds(bounds) {
        // We don't know how to deal with this when an animation is in progress so
        // we either cancel it or run it to completion.
        this.stopCurrentAnimation(StopAnimationKind.FORCE);
        this.derivedBounds = Object.assign({}, bounds);
        this.dealWithBoundsChanges();
    }
    setBoundsToContainer() {
        // We don't know how to deal with this when an animation is in progress so
        // we either cancel it or run it to completion.
        this.stopCurrentAnimation(StopAnimationKind.FORCE);
        this.derivedBounds = {
            x: [0, this.workingValues.containerWidth],
            y: [0, this.workingValues.containerHeight],
            // Can't zoom out but you can zoom in
            zoom: [1, undefined],
        };
        this.dealWithBoundsChanges();
    }
    updateTopLeft(x, y, newZoomFactor, animationOptions) {
        if (!this.stopCurrentAnimation(StopAnimationKind.INTERRUPT)) {
            return;
        }
        const updateTarget = !animationOptions ? this.workingValues : Object.assign({}, this.workingValues);
        if (newZoomFactor) {
            ViewPortMath_1.ViewPortMath.updateZoom(updateTarget, this.derivedBounds, newZoomFactor);
        }
        ViewPortMath_1.ViewPortMath.updateTopLeft(updateTarget, this.derivedBounds, x, y);
        if (!animationOptions) {
            this.scheduleHardUpdate();
        }
        else {
            this.scheduleAnimation(updateTarget, animationOptions);
        }
    }
    advanceCurrentAnimation(percent) {
        if (!this.animation) {
            return;
        }
        const { targetValues: tv, startingValues: sv } = this.animation;
        if (percent >= 1) {
            this.copyValues(tv, this.workingValues);
        }
        else {
            // Simple ease out quartic
            const z = 1 - percent;
            const p = 1 - z * z * z * z;
            // The reason we use `updateBy` with deltas is that when changing the zoom
            // factor, sometimes, like when it is already small, there is some weird
            // effect the math has where the animation appears to go down and to the
            // right quite a bit before coming back up and to the left. Totally
            // bizarre. Not really sure why to be honest, but I think it is due to us
            // not being able to adjust the anchor position when the zoom changes, and
            // thus getting the wrong x and y positions.
            // Anyways, doing small updateBys like this is easier and is similar to
            // what happens when zooming in and out with the mouse wheel.
            const dx = utils_1.transitionNumber(sv.centerX, tv.centerX, p) - this.workingValues.centerX;
            const dy = utils_1.transitionNumber(sv.centerY, tv.centerY, p) - this.workingValues.centerY;
            const dz = utils_1.transitionNumber(sv.zoomFactor, tv.zoomFactor, p) - this.workingValues.zoomFactor;
            ViewPortMath_1.ViewPortMath.updateBy(this.workingValues, this.derivedBounds, dx, dy, dz);
        }
    }
    scheduleAnimation(targetValues, animationOptions) {
        ts_invariant_1.invariant(!this.animation, 'Cannot schedule animation while another animation is still in progress.');
        this.animation = Object.assign({ startingValues: Object.assign({}, this.workingValues), targetValues, 
            // We don't have a good way to get the high-res time that will be passed
            // to requestAnimationFrame (performance.now() is greater than the next
            // time we get in requestAnimationFrame for some reason, sometimes). So we
            // set this to null and deal with it in handleAnimationFrame.
            startingTimeMilliseconds: undefined }, animationOptions);
        if (!this.animationFrameId) {
            this.animationFrameId = requestAnimationFrame(this.handleAnimationFrame);
        }
    }
    scheduleHardUpdate() {
        ts_invariant_1.invariant(!this.animation, 'Cannot schedule update while an animation is still in progress.');
        // If there was a pending animation it should have been committed before
        // this was called (so that the working values could have been updated)
        if (!this.animationFrameId) {
            this.animationFrameId = requestAnimationFrame(this.handleAnimationFrame);
        }
    }
    stopCurrentAnimation(stopKind) {
        if (this.animation) {
            if (this.animation.preventInterruption) {
                if (stopKind === StopAnimationKind.FORCE) {
                    this.copyValues(this.animation.targetValues, this.workingValues);
                }
                else {
                    return false;
                }
            }
            this.animation = undefined;
        }
        return true;
    }
}
exports.ViewPortCamera = ViewPortCamera;
//# sourceMappingURL=ViewPortCamera.js.map